import { FunctionComponent, ChangeEvent, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useIntl, defineMessages } from 'dibs-react-intl';
import { Input } from 'dibs-elements/exports/Input';
import { VARS } from '../../constants/checkoutConstants';
import { useCheckoutSelector } from '../../reducers/root';
import { CheckoutMobileSummaryQuantitySelect_transaction$key } from './__generated__/CheckoutMobileSummaryQuantitySelect_transaction.graphql';
import styles from './styles/CheckoutMobileSummaryPriceTable.scss';

type CheckoutMobileSummaryQuantitySelectProps = {
    transaction: CheckoutMobileSummaryQuantitySelect_transaction$key;
    quantity: number;
    onChange: (quantity: number) => void;
};

const messages = defineMessages({
    quantityLabel: {
        id: 'bomt.CheckoutMobileSummaryQuantitySelect.quantityLabel',
        defaultMessage: '{quantity} {quantity, plural, one {item} other {items}}',
    },
});

export const CheckoutMobileSummaryQuantitySelect: FunctionComponent<
    CheckoutMobileSummaryQuantitySelectProps
> = props => {
    const { onChange, quantity } = props;

    const intl = useIntl();
    const [isInputtingCustomQuantity, setIsInputtingCustomQuantity] = useState(false);
    const [customQuantityValue, setCustomQuantityValue] = useState('');

    const isProcessing = useCheckoutSelector(state => state.ui.isProcessing) || false;

    const transaction = useFragment(
        graphql`
            fragment CheckoutMobileSummaryQuantitySelect_transaction on Transaction {
                sku {
                    availableQuantity: quantity
                    hasUnlimitedQuantity
                    quantitySelectOptions {
                        label
                        value
                    }
                    setSize
                }
            }
        `,
        props.transaction
    );

    const sku = transaction?.sku;
    const quantityOptions = sku?.quantitySelectOptions || [];
    const setSize = sku?.setSize || 1;

    function handleQuantityChange(
        event: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>
    ): void {
        const newQuantity = Number(event.target.value);
        const customQuantityOptionValue = quantityOptions?.[quantityOptions.length - 1]?.value;
        const availableQuantity = sku?.availableQuantity;
        const hasUnlimitedQuantity = sku?.hasUnlimitedQuantity;

        const isNewQuantityValid =
            newQuantity > 0 && newQuantity <= (availableQuantity || VARS.QUANTITY_LIMIT);

        if (isInputtingCustomQuantity) {
            setCustomQuantityValue(event.target.value);
        }

        if (!isNewQuantityValid) {
            return;
        }

        if (
            !isInputtingCustomQuantity &&
            hasUnlimitedQuantity &&
            newQuantity === customQuantityOptionValue
        ) {
            setIsInputtingCustomQuantity(true);
            setCustomQuantityValue('');
        } else if (newQuantity !== quantity) {
            onChange(newQuantity);
        }
    }

    const currentSelectOption = quantityOptions.find(option => option?.value === quantity);

    return (
        <span className={styles.quantitySelectWrapper}>
            {isInputtingCustomQuantity ? (
                <Input
                    size="small"
                    value={customQuantityValue}
                    onChange={handleQuantityChange}
                    dataTn="custom-quantity-input"
                />
            ) : (
                /* this is a dummy element that will be visible to the user, but is uninteractable and lets all the pointer events through
                   to the "hidden" select behind it. This is needed to correctly position the select label and the "×" symbol; when
                   using a single select, problems arise from the fact the selects are as wide as the widest option, creating unnecessary
                   space when the currently selected option is shorter, and the fact `text-align: right` does not work for selects on Safari.
                 */
                <>
                    <span className={styles.displayQuantitySelect}>
                        ×{' '}
                        <span className={styles.displayQuantityLabel}>
                            {setSize > 1
                                ? currentSelectOption?.label
                                : intl.formatMessage(messages.quantityLabel, {
                                      quantity: currentSelectOption?.label,
                                  })}
                        </span>
                    </span>
                    <select
                        className={styles.quantitySelect}
                        value={quantity}
                        onChange={handleQuantityChange}
                        disabled={isProcessing}
                    >
                        {quantityOptions.map(option => {
                            const { value, label } = option || {};
                            if (!label || !value) {
                                return null;
                            }
                            return (
                                <option key={`quantity-${value}`} value={value}>
                                    {setSize > 1
                                        ? label
                                        : intl.formatMessage(messages.quantityLabel, {
                                              quantity: label,
                                          })}
                                </option>
                            );
                        })}
                    </select>
                </>
            )}
        </span>
    );
};
